/**
 * THE THREE-LEVEL BOARD NOTIFICATIONS BUTTON (task #31, Bobbie 2026-08-30,
 * verbatim, the Jr Staff board screenshot session): "I think we need a set
 * notifications for this board on every board button also. Notifications
 * for everything / Notifications just for original post / No notifications
 * for anything in this board." There is no separate "Leave board" control -
 * level 3 IS it, and that was her own earlier answer to "what happens when
 * someone leaves": **"Just stop the pings."** Access never changes; only
 * the pings do.
 *
 * Storage rides notifPrefs.mutedIds (PR #797's probed rules shape, landed in
 * shared/firestore.rules' hasNotifPrefsShape by this same task): the field
 * holds what is OFF, never what is on, so absent and empty both mean
 * everything, and no write ever opts anyone IN to a board they were not
 * already reading.
 *
 * BOARD_KEY IS THE NOTIFY PATH'S OWN `lane` STRING, on purpose - see
 * hub/netlify/functions/lib/notificationAuthority.mjs's authorityLane() and
 * its BOARD_NOTIFICATION_MUTE_LANE_KEYS map. Nothing here invents a second
 * name for the same board.
 */

export type BoardKey = 'compBoard' | 'jrStaffBoard' | 'parentBoard' | 'compParentsBoard';

export const BOARD_KEYS: readonly BoardKey[] = [
  'compBoard', 'jrStaffBoard', 'parentBoard',
  // THE COMP PARENTS BOARD (packet R43, ruling 9b). It is a board with a
  // notification level like any other, so it belongs in this union - but it
  // is deliberately NOT a MembershipOverlayKey in ./boardMembershipOverlay:
  // its membership is derived server-side behind
  // netlify/functions/compParentsBoard.mjs, with no client-readable overlay
  // doc to watch and no rules entry to read one through. That exclusion is
  // type-enforced there rather than left to a comment here.
  'compParentsBoard',
];

/**
 * THE SUB BOARD JOINS THE SETTING, NOT THE BOARDS. Bobbie, 2026-09-04: "We
 * need to be able to to select if you want to see notifications on the sub
 * boards as well."
 *
 * It is deliberately NOT added to BoardKey, for the same reason the Command
 * Center is not (see boardMembershipOverlay.ts): BoardKey drives
 * OVERLAY_COLLECTION, and the Sub Board has no membership overlay doc, no
 * roll, and nobody to add or remove. Widening THIS key instead gives it the
 * stored preference, the same mutedIds storage and the same button, with no
 * phantom fourth overlay collection.
 *
 * 'sub' is the notify path's own `lane` string, same rule as BOARD_KEY above:
 * see BOARD_NOTIFICATION_MUTE_LANE_KEYS in notificationAuthority.mjs.
 */
export type BagGroupNotificationKey = `bagGroup:${string}`;
/**
 * ONE CLASS'S OWN NOTIFICATION KEY (Bobbie, 2026-09-28: "can we add the
 * selector at the top of the page? where they get to pick the notification
 * type", and, asked which page, "Every board, same spot" - every board AND
 * every class page).
 *
 * Per class, like a bag group is per group: muting Junior Jazz must never
 * quiet Senior Tap. The offering id is encoded the same way a bag group id
 * is, so a class id can never manufacture a ':replies' suffix of its own.
 *
 * THE THREE LEVELS ARE REAL ON THE SERVER, checked before they were offered:
 * a class POST and a class REPLY are two separate alert lanes (classPostId
 * alone, classPostId plus classReplyId), so "just for original post" drops
 * the reply alerts and keeps the post alerts, and "no notifications" drops
 * both. Every lane that sends either one reads this key: see
 * isClassNotificationMuted in netlify/functions/lib/notificationAuthority.mjs.
 *
 * Like 'sub' and a bag group, it is not a BoardKey: a class has no membership
 * overlay and must never grow one (enrollment and staff assignment already
 * answer who is in it). ZERO rules bytes, same as the rest of mutedIds.
 */
export type ClassNotificationKey = `class:${string}`;
/**
 * THE TWO PRIVATE TEAM BOARDS (the Directors + Managers + Executive Teachers
 * board and the Interns board). Both are discussion boards drawn by
 * PrivateTeamBoard in src/pages/Interns.tsx, with posts and replies, so they
 * take the same three levels as every other board (Bobbie, 2026-09-28,
 * "Every board, same spot"). Each key is that board's own boardCovers key,
 * so nothing here invents a second name for the same board. Like 'sub', not
 * a BoardKey: neither board has a membership overlay, and roles and the
 * 'interns' group already answer who is on it. The server reads the same
 * two keys in isBoardNotificationMuted's discussion branch
 * (netlify/functions/lib/notificationAuthority.mjs).
 */
export type PrivateTeamBoardNotifyKey = 'executiveBoard' | 'interns';
export const PRIVATE_TEAM_BOARD_NOTIFY_KEYS: readonly PrivateTeamBoardNotifyKey[] = ['executiveBoard', 'interns'];
export type NotifiableBoardKey =
  | BoardKey | 'sub' | 'adminBoard' | PrivateTeamBoardNotifyKey
  | BagGroupNotificationKey | ClassNotificationKey | BigLittleNotifyKey;

export const SUB_BOARD_NOTIFY_KEY = 'sub' as const;

/**
 * THE BIG/LITTLE BOARD (DECISIONS 130). Its posts alert the other dancers in
 * the group and their grown-ups (netlify/functions/lib/compPairAlerts.mjs),
 * and the board takes the same selector as every board (Bobbie, 2026-09-28,
 * "Every board, same spot"). Like 'sub', a group board has no replies to
 * separate, so it offers two levels, not three. Like 'sub' and a bag group,
 * it is not a BoardKey: no membership overlay, the published groups decide
 * who reads it. The server reads the same string (BIG_LITTLE_NOTIFY_KEY).
 */
export type BigLittleNotifyKey = 'bigLittle';
export const BIG_LITTLE_NOTIFY_KEY: BigLittleNotifyKey = 'bigLittle';

/**
 * The bag group's own notification key. Same three levels as a board, but the
 * middle one means something different here and Bobbie named it herself,
 * 2026-09-04: "All notifications / just from the lead / no notifications."
 *
 * A bag group has no replies to separate from original posts; what its members
 * want to separate is the lead's word from the chatter. So the middle level
 * asks WHO POSTED rather than what kind of post it was, and the copy says so.
 * Who counts is the server's one rule, bagAuthorCountsAsLead in
 * netlify/functions/lib/bagLeadLevel.mjs: the group's lead, and since
 * 2026-10-08 a Director, Admin or Manager too (DECISIONS 126 item 1, "Lead +
 * studio staff"). A group with no lead keeps every post at this level.
 *
 * Like 'sub', it is not a BoardKey: there is no membership overlay collection
 * for a bag group and it must never grow one. And like the isLead flag it
 * reads, it costs ZERO rules bytes - mutedIds is validated by SIZE only.
 */
export function bagGroupNotificationKey(groupId: string): BagGroupNotificationKey {
  return `bagGroup:${encodeURIComponent(groupId)}`;
}

function isBagGroupNotificationKey(boardKey: NotifiableBoardKey): boardKey is BagGroupNotificationKey {
  return boardKey.startsWith('bagGroup:');
}

/** One class's key. See ClassNotificationKey above for why it is encoded. */
export function classNotificationKey(offeringId: string): ClassNotificationKey {
  return `class:${encodeURIComponent(offeringId)}`;
}

export function isClassNotificationKey(boardKey: string): boardKey is ClassNotificationKey {
  return boardKey.startsWith('class:') && boardKey.length > 'class:'.length;
}

/**
 * THE ONE DEFINITION OF WHO STARTS AT OFF ON THE JR STAFF BOARD, for the
 * server and the client alike (notificationAuthority.mjs's
 * jrStaffBoardMuteContext calls this same function). The answer is nobody.
 *
 * Bobbie, 2026-09-29, verbatim, about the Jr Staff board: "actauly now
 * default to all and then send out a notification to all jr staff parents
 * letting them know they can now select the type of notiiations for this
 * board". That supersedes her 2026-09-28 "leave it as off" (DECISIONS 64's
 * board half, PR #1264). Her other two answers in the same set:
 *   - a staff member who is also a Jr Staff's parent: "Everything, like
 *     staff" (no change from before);
 *   - a family account a Director adds to the board by hand who is NOT a Jr
 *     Staff parent: "Start at Everything".
 * So every viewer, staff or family, starts at Everything.
 *
 * A pick a parent already MADE is kept exactly as stored. An explicit "No
 * notifications" (the plain key in her mute list) and "original posts only"
 * (the replies entry, with or without the opt-in marker) both stay, on the
 * selector and on the send alike, so nobody is switched on who chose Off.
 * Only a parent who never picked moves, and she moves to Everything.
 *
 * WHY THE FUNCTION STILL EXISTS. The server (jrStaffBoardMuteContext, the
 * send, the evening summary) and the client selector (boardStartsOffFor) all
 * ask this one function, so they cannot disagree about a default. It is kept,
 * with its two arguments, so a future ruling changes one line and not four
 * call sites. The reach fallback (lib/jrStaffBoardReach.mjs) still computes
 * whether every Jr Staff of a parent is reached on a sign-in of her own, and
 * it no longer changes anyone's default: the answer is passed in and ignored.
 */
export function jrStaffBoardStartsOff(
  _viewerIsStaff: boolean,
  _everyJrStaffReached: boolean | undefined
): boolean {
  return false;
}

/**
 * A BOARD THAT STARTS AT OFF FOR THIS VIEWER. No board does today: the Jr
 * Staff board asks jrStaffBoardStartsOff just above, which is false for every
 * viewer, so this is false for every board and every viewer.
 */
export function boardStartsOffFor(
  boardKey: NotifiableBoardKey,
  viewerIsStaff: boolean,
  everyJrStaffReached?: boolean
): boolean {
  return boardKey === 'jrStaffBoard' && jrStaffBoardStartsOff(viewerIsStaff, everyJrStaffReached);
}

/**
 * EVERY PARENT'S PICK ON THE JR STAFF BOARD CARRIES THE OPT-IN MARKER, whether
 * her default is Everything (as it is today) or ever Off again. A pick she
 * made must mean the same thing on both sides of any change of default: "A
 * parent who picks a level keeps it." A staff viewer's pick is written the
 * ordinary way, unchanged.
 */
export function boardPickCarriesOptIn(boardKey: NotifiableBoardKey, viewerIsStaff: boolean): boolean {
  return boardKey === 'jrStaffBoard' && !viewerIsStaff;
}

export interface BoardNotificationLevelOptions {
  /** True when this viewer's default on this board is Off (boardStartsOffFor).
   *  False for every viewer today; the option stays so a default that is Off
   *  again later reads through the same code. */
  startsOff?: boolean;
  /** True when a pick here is written with the opt-in marker (boardPickCarriesOptIn). */
  optIn?: boolean;
}

/**
 * THE OPT-IN MARKER for a board whose default can be Off. It came in with the
 * Off default of 2026-09-28 on purpose: a parent's stored state from before
 * then never carries it, and Everything from before left nothing stored at
 * all, so it never left her on without her picking. One stored choice from
 * before DOES still count: "original posts only", whose replies entry only an
 * explicit pick ever wrote (ownBoardNotificationPick below). With the default
 * back at Everything (2026-09-29) the marker alone reads as Everything, which
 * is also what no entry at all reads as.
 */
function optInEntry(boardKey: NotifiableBoardKey): string {
  return `${boardKey}:on`;
}

/**
 * The words for one level on one board. Bobbie gave the three board strings
 * verbatim in 2026-08-30 and the bag group's three in 2026-09-04; neither set
 * is paraphrased, and a board with no override keeps the originals.
 */
const BAG_GROUP_NOTIFICATION_LEVEL_LABEL: Record<BoardNotificationLevel, string> = {
  everything: 'All notifications',
  originalOnly: 'Just from the lead',
  nothing: 'No notifications',
};

export function notificationLevelLabel(
  boardKey: NotifiableBoardKey,
  level: BoardNotificationLevel
): string {
  return isBagGroupNotificationKey(boardKey)
    ? BAG_GROUP_NOTIFICATION_LEVEL_LABEL[level]
    : BOARD_NOTIFICATION_LEVEL_LABEL[level];
}

export type BoardNotificationLevel = 'everything' | 'originalOnly' | 'nothing';

export const BOARD_NOTIFICATION_LEVELS: readonly BoardNotificationLevel[] = [
  'everything',
  'originalOnly',
  'nothing',
];

/** The exact copy Bobbie gave for each level, verbatim - do not paraphrase. */
export const BOARD_NOTIFICATION_LEVEL_LABEL: Record<BoardNotificationLevel, string> = {
  everything: 'Notifications for everything',
  originalOnly: 'Notifications just for original post',
  nothing: 'No notifications for anything in this board',
};

/**
 * Which levels this board can actually offer.
 *
 * A sub request has NO REPLY CONCEPT - the board is requests and claims, not
 * threads - so "just for original post" would be a setting that changes
 * nothing, and an option that does nothing is a lie in a menu. Same shape the
 * server already applies to compTip, which is likewise always an original.
 * The two remaining labels are Bobbie's own verbatim copy, unchanged.
 */
export function notificationLevelsFor(
  boardKey: NotifiableBoardKey
): readonly BoardNotificationLevel[] {
  return boardKey === SUB_BOARD_NOTIFY_KEY || boardKey === BIG_LITTLE_NOTIFY_KEY
    ? (['everything', 'nothing'] as const)
    : BOARD_NOTIFICATION_LEVELS;
}

/**
 * THE SAME CHOICES, SHORT ENOUGH TO SIT INSIDE THE BUTTON (Bobbie,
 * 2026-09-02, with a screenshot of the open menu: "This is not obvious to
 * parents yet this is a selection option... Can you Make it clear it is a
 * selector?"). A selector shows its current value; the full sentences above
 * do not fit on a 390px button beside three others.
 *
 * THE LABELS ABOVE ARE STILL WHAT THE MENU SAYS, verbatim and unparaphrased.
 * These are the trigger's summary of the SAME value and nowhere else, which
 * is why they are a separate map instead of an edit to that one. A bag group
 * gets its own summaries for the same reason it gets its own labels: its
 * middle level asks WHO POSTED, so "original posts only" would be the wrong
 * summary of Bobbie's "Just from the lead".
 */
const BOARD_NOTIFICATION_LEVEL_SHORT: Record<BoardNotificationLevel, string> = {
  everything: 'everything',
  originalOnly: 'original posts only',
  nothing: 'off',
};

/**
 * "lead + studio", not "the lead only" (BG-4). DECISIONS 126 item 1: Bobbie
 * picked "Lead + studio staff (Recommended)", offered as "Posts from the
 * group's lead AND from Directors, Admin and Managers still alert them.", and
 * BG-1 (#1360) made the delivery do exactly that. "the lead only" then
 * described a level that no longer exists. The menu row keeps her verbatim
 * "Just from the lead"; only this button summary changed, and it is no longer
 * than "original posts only", which every other board already fits on a
 * 390px phone.
 */
const BAG_GROUP_NOTIFICATION_LEVEL_SHORT: Record<BoardNotificationLevel, string> = {
  everything: 'everything',
  originalOnly: 'lead + studio',
  nothing: 'off',
};

/** The trigger's summary of the current level, board-aware the same way
 *  notificationLevelLabel is. Never used inside the menu itself. */
export function notificationLevelShortLabel(
  boardKey: NotifiableBoardKey,
  level: BoardNotificationLevel
): string {
  return isBagGroupNotificationKey(boardKey)
    ? BAG_GROUP_NOTIFICATION_LEVEL_SHORT[level]
    : BOARD_NOTIFICATION_LEVEL_SHORT[level];
}

/**
 * THE CATEGORY NOTE BESIDE THE LEVEL (BG-4, plan defect N5). A bag group's
 * phone, email and text alerts ride the account's Messages & to-dos switch
 * (notify.mjs KIND_CATEGORY), so a parent who turned that off for another
 * reason saw "Notifications: everything" on her group while only the bell
 * still arrived. The page that opts in passes its own words (only the bag
 * group page does, so every other board draws exactly what it drew before),
 * and the note shows only while:
 *   - the board's own category is explicitly off for this active account
 *     (boardPhoneAlertStatus's 'category-off', the same check the open menu
 *     already reports), and
 *   - her level still sends something. At "off" the note would only repeat it,
 *     and an unknown level ("tap to choose") makes no claim at all.
 */
export function boardCategoryOffNote(
  note: string | undefined,
  phoneAlertState: string,
  level: BoardNotificationLevel | null
): string | null {
  if (!note || phoneAlertState !== 'category-off' || !level || level === 'nothing') return null;
  return note;
}

function repliesEntry(boardKey: NotifiableBoardKey): string {
  return `${boardKey}:replies`;
}

function explicitEverythingEntry(boardKey: BagGroupNotificationKey): string {
  return `${boardKey}:all`;
}

/**
 * THE LEVEL A PERSON PICKED HERSELF on a board whose default can be Off (the
 * Jr Staff board, whose default is Everything today), or null when her stored
 * list holds no pick of her own and her default decides. ONE rule for the
 * board page's selector, the send path (isBoardNotificationMuted in
 * netlify/functions/lib/notificationAuthority.mjs) and the evening summary
 * (digestItemsForTarget in netlify/functions/digest.mjs), so the three cannot
 * drift.
 *
 *   - the plain key is Off, and her Off wins over any other entry;
 *   - the replies entry is "original posts only", WITH OR WITHOUT the opt-in
 *     marker. Only an explicit choice ever writes it, and parents have had
 *     that choice on the Jr Staff board since #805, long before the Off
 *     default existed. So a stored replies entry with no marker is a real
 *     pick from before the marker, and it is kept (refute of PR #1264: "Any
 *     level the parent picks herself wins", and DECISIONS 64 silences only a
 *     parent who never touches it);
 *   - the opt-in marker alone is Everything.
 *
 * Not for a bag group key, whose middle level and legacy global key read
 * differently (boardNotificationLevel below).
 */
export function ownBoardNotificationPick(
  mutedIds: string[] | undefined,
  boardKey: NotifiableBoardKey
): BoardNotificationLevel | null {
  const list = Array.isArray(mutedIds) ? mutedIds : [];
  if (list.includes(boardKey)) return 'nothing';
  if (list.includes(repliesEntry(boardKey))) return 'originalOnly';
  if (list.includes(optInEntry(boardKey))) return 'everything';
  return null;
}

/**
 * True when her stored list already carries the opt-in marker for this board,
 * so an "on" level she is shown is her own pick and not only a default that
 * can move. The selector writes the marker when she taps an "on" level that
 * lacks it, even if it looks like the level she is already at.
 */
export function hasBoardNotificationOptIn(
  mutedIds: string[] | undefined,
  boardKey: NotifiableBoardKey
): boolean {
  return Array.isArray(mutedIds) && mutedIds.includes(optInEntry(boardKey));
}

/**
 * What level a person is currently at for one board, read off their stored
 * mutedIds. A malformed or missing list reads as 'everything' - the same
 * fail-open-to-the-default the rules themselves apply (absent means on).
 */
export function boardNotificationLevel(
  mutedIds: string[] | undefined,
  boardKey: NotifiableBoardKey,
  options: BoardNotificationLevelOptions = {}
): BoardNotificationLevel {
  const list = Array.isArray(mutedIds) ? mutedIds : [];
  if (options.startsOff) {
    // Off until she picks. Her own pick, read by the one shared rule above.
    return ownBoardNotificationPick(list, boardKey) ?? 'nothing';
  }
  if (isBagGroupNotificationKey(boardKey)) {
    if (list.includes(explicitEverythingEntry(boardKey))) return 'everything';
    if (list.includes(boardKey)) return 'nothing';
    if (list.includes(repliesEntry(boardKey))) return 'originalOnly';
    // The one-day global key remains a compatibility fallback only until a
    // member makes an explicit choice for this group. New writes are always
    // group-scoped, so changing Green Tote B cannot change Green Tote A.
    if (list.includes('bagGroup')) return 'nothing';
    if (list.includes('bagGroup:replies')) return 'originalOnly';
    return 'everything';
  }
  if (list.includes(boardKey)) return 'nothing';
  if (list.includes(repliesEntry(boardKey))) return 'originalOnly';
  return 'everything';
}

/**
 * The mutedIds list that results from setting ONE board to a new level,
 * every other board's entry left untouched. Never mutates the input.
 */
export function withBoardNotificationLevel(
  mutedIds: string[] | undefined,
  boardKey: NotifiableBoardKey,
  level: BoardNotificationLevel,
  options: BoardNotificationLevelOptions = {}
): string[] {
  const rest = (Array.isArray(mutedIds) ? mutedIds : [])
    .filter((id) => id !== boardKey
      && id !== repliesEntry(boardKey)
      && id !== optInEntry(boardKey)
      && (!isBagGroupNotificationKey(boardKey) || id !== explicitEverythingEntry(boardKey)));
  if (level === 'nothing') return [...rest, boardKey];
  if (options.startsOff || options.optIn) {
    // Her own pick, written with the marker that says she made it, so it
    // reads the same whichever default she has when it is read back.
    return level === 'originalOnly'
      ? [...rest, optInEntry(boardKey), repliesEntry(boardKey)]
      : [...rest, optInEntry(boardKey)];
  }
  if (level === 'originalOnly') return [...rest, repliesEntry(boardKey)];
  // For bag groups, absence could mean an inherited legacy global mute. Keep
  // an explicit per-group "all" marker so the member can override it without
  // changing any other group.
  if (isBagGroupNotificationKey(boardKey)) {
    return [...rest, explicitEverythingEntry(boardKey)];
  }
  return rest;
}

/**
 * PACKET J30's shared write gate. netlify/functions/lib/boardNotificationLevel.mjs
 * is the ONE place left that writes notifPrefs.mutedIds for a Jr Staff with
 * more than one sign-in, and unlike the old client path - which never had to
 * validate boardKey, because shared/firestore.rules' hasNotifPrefsShape only
 * caps mutedIds by SIZE, never by content - that function is now the whole
 * gate. A hostile or stale client body naming a boardKey that is not one of
 * these is refused before anything is read or written, rather than accepted
 * into an otherwise-legitimate person's own mute list.
 * @param {unknown} value
 * @returns {value is NotifiableBoardKey}
 */
const NAMED_NOTIFIABLE_BOARD_KEYS: ReadonlySet<string> = new Set<string>([
  ...BOARD_KEYS, SUB_BOARD_NOTIFY_KEY, 'adminBoard', ...PRIVATE_TEAM_BOARD_NOTIFY_KEYS, BIG_LITTLE_NOTIFY_KEY,
]);

export function isNotifiableBoardKey(value: unknown): value is NotifiableBoardKey {
  if (typeof value !== 'string' || value.length === 0 || value.length > 300) return false;
  if (NAMED_NOTIFIABLE_BOARD_KEYS.has(value)) return true;
  // A class key is accepted by shape, the way a bag group key is, and for the
  // same reason: a Jr Staff's class choice rides this same canonical write so
  // it lands on both of her sign-ins (DECISIONS 26 and 27). The encoded id
  // can never carry a raw ':' so a suffix cannot be smuggled in with it.
  if (isClassNotificationKey(value)) return !value.slice('class:'.length).includes(':');
  return value.startsWith('bagGroup:') && value.length > 'bagGroup:'.length;
}

/**
 * WHICH ACCOUNT CATEGORY A BOARD'S ALERTS ACTUALLY RIDE ON.
 *
 * Audit lane B, 2026-09-17, finding B3. The board notifications button asked
 * `notifPrefs.messages` for EVERY board, hardcoded, while the server routes a
 * Sub Board alert through KIND_CATEGORY.sub, which is 'calendar'. Both
 * directions were wrong on that one board: a teacher who turned off Calendar
 * changes was told her phone alerts were ready and never got one, and a
 * teacher who turned off Messages & to-dos was told sub alerts were off and
 * kept getting them by email and push. That is the failure that produces "I
 * never get notified and my settings look fine".
 *
 * The map below is the client's half of the same `lane === source key`
 * discipline BOARD_NOTIFICATION_MUTE_LANE_KEYS already uses: the notify kind
 * each board's own producer sends, then the server's own KIND_CATEGORY for
 * that kind. It is pinned against the server map by test rather than kept in
 * step by hand.
 */
export type BoardNotificationCategory = 'messages' | 'calendar' | 'comp';

/** The `kind` each board's producer really sends, one entry per board key. */
export const BOARD_NOTIFICATION_KIND = {
  // src/lib/compBoard.ts, src/lib/compTips.ts
  compBoard: 'message',
  // src/lib/jrStaffBoard.ts
  jrStaffBoard: 'board',
  // src/lib/parentBoard.ts
  parentBoard: 'board',
  // src/lib/compParentsBoard.ts
  compParentsBoard: 'board',
  // src/lib/subs.ts
  sub: 'sub',
  // src/pages/AdminBoard.tsx
  adminBoard: 'message',
  // notify.mjs's notifyDiscussion action, the canonical payload it writes
  // for both private team boards (src/pages/Interns.tsx)
  executiveBoard: 'board',
  interns: 'board',
  // src/lib/bagGroups.ts
  bagGroup: 'bag-group',
  // src/lib/classPosts.ts and every class post and reply lane on the server
  class: 'message',
  // netlify/functions/lib/compPairAlerts.mjs (COMP_PAIR_ALERT_KIND)
  bigLittle: 'board',
} as const;

/** The client mirror of KIND_CATEGORY in hub/netlify/functions/notify.mjs. */
const KIND_CATEGORY: Record<string, BoardNotificationCategory> = {
  message: 'messages',
  mention: 'messages',
  task: 'messages',
  bulletin: 'messages',
  newsletter: 'messages',
  board: 'messages',
  'bag-group': 'messages',
  attendance: 'messages',
  calendar: 'calendar',
  sub: 'calendar',
  comp: 'comp',
};

/** The account setting one board's alerts actually depend on. */
export function boardNotificationCategory(boardKey: NotifiableBoardKey): BoardNotificationCategory {
  const key = isBagGroupNotificationKey(boardKey)
    ? 'bagGroup'
    : isClassNotificationKey(boardKey) ? 'class' : boardKey;
  return KIND_CATEGORY[BOARD_NOTIFICATION_KIND[key]];
}
